import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useStore } from '../context/StoreContext';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/useToast';
import { useScrollReveal } from '../hooks/useParallax';
import ToastContainer from '../components/Toast';
import ProductCard from '../components/ProductCard';
import { safeProductId } from '../utils/security';
import reviewsData from '../data/reviews.json';
import api from '../utils/api';
import { normalizeProduct, slugifyCategoryName } from '../utils/productUtils';

const ANGLES = [
  { key: 'front', label: 'Front View' },
  { key: 'right', label: 'Right View' },
  { key: 'left',  label: 'Left View'  },
  { key: 'top',   label: 'Top View'   },
  { key: 'detail', label: 'Detail'     },
];

function StarBar({ label, count, max }) {
  const pct = max > 0 ? (count / max) * 100 : 0;
  return (
    <div className="rating-bar-row">
      <span className="starbar-label">{label}</span>
      <i className="fas fa-star starbar-icon" />
      <div className="rating-bar-track">
        <div className="rating-bar-fill" style={{ width: `${pct}%` }} />
      </div>
      <span className="starbar-count">{count}</span>
    </div>
  );
}

function formatReviewDate(dateValue) {
  if (!dateValue) return '--';
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return '--';
  return date.toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: '2-digit' });
}

export default function ProductDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const safeId = safeProductId(id);
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeAngle, setActiveAngle] = useState('front');
  const [selectedColor, setSelectedColor] = useState('');
  const [selectedSize, setSelectedSize] = useState('');
  const [selectedMaterial, setSelectedMaterial] = useState('');
  const [qty, setQty] = useState(1);
  const [activeTab, setActiveTab] = useState('description');
  const wrapRef = useRef(null);
  const { addToCart, toggleWishlist, isInWishlist } = useStore();
  const { user, isAuthenticated } = useAuth();
  const { toasts, showToast } = useToast();
  useScrollReveal();

  // Review form state
  const [reviewForm, setReviewForm] = useState({
    rating: 5,
    comment: '',
  });
  const [submitReviewLoading, setSubmitReviewLoading] = useState(false);
  const [submitReviewMessage, setSubmitReviewMessage] = useState({ type: '', text: '' });

  // User's reviews state
  const [userReviews, setUserReviews] = useState([]);
  const [userReviewsLoading, setUserReviewsLoading] = useState(true);
  const [userReviewsError, setUserReviewsError] = useState('');

  useEffect(() => {
    if (!safeId) {
      navigate('/shop');
      return undefined;
    }

    let ignore = false;

    const loadProduct = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await api.get(`/products/${safeId}`, {
          headers: { 'Cache-Control': 'no-cache' },
        });
        const loadedProduct = normalizeProduct(response.data.product || {});

        if (ignore) {
          return;
        }

        setProduct(loadedProduct);
        setSelectedColor(loadedProduct.colors[0] || '');
        setSelectedSize(loadedProduct.sizes[0] || '');
        setSelectedMaterial(loadedProduct.materials[0] || '');
        setActiveAngle('front');
        window.scrollTo({ top: 0, behavior: 'smooth' });

        try {
          const relatedResponse = await api.get('/products', {
            params: { category: loadedProduct.category },
            headers: { 'Cache-Control': 'no-cache' },
          });

          if (!ignore) {
            setRelated(
              (relatedResponse.data.products || [])
                .map(normalizeProduct)
                .filter((item) => item.id !== loadedProduct.id)
                .slice(0, 4)
            );
          }
        } catch (relatedError) {
          console.error(relatedError);
          if (!ignore) {
            setRelated([]);
          }
        }
      } catch (fetchError) {
        console.error(fetchError);
        if (!ignore) {
          setProduct(null);
          setRelated([]);
          setError(
            fetchError.response?.data?.message || fetchError.message || 'Failed to load product'
          );
        }
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    };

    loadProduct();

    return () => {
      ignore = true;
    };
  }, [safeId, navigate]);

  // Fetch user's reviews
  useEffect(() => {
    if (!isAuthenticated) {
      setUserReviews([]);
      setUserReviewsLoading(false);
      return;
    }

    const fetchUserReviews = async () => {
      setUserReviewsLoading(true);
      setUserReviewsError('');
      try {
        const response = await api.get('/reviews/my-reviews');
        if (response.data?.success) {
          setUserReviews(response.data.reviews || []);
        } else {
          setUserReviews([]);
        }
      } catch (error) {
        console.error(error);
        setUserReviews([]);
        setUserReviewsError('Unable to load your reviews');
      } finally {
        setUserReviewsLoading(false);
      }
    };

    fetchUserReviews();
  }, [isAuthenticated]);

  const handleMouseMove = useCallback((e) => {
    if (!wrapRef.current) return;
    const rect = wrapRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    wrapRef.current.style.setProperty('--mx', `${x}%`);
    wrapRef.current.style.setProperty('--my', `${y}%`);
  }, []);

  if (loading) {
    return (
      <div className="detail-page-container">
        <div className="container py-5">
          <p className="text-center mb-0">Loading product...</p>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="detail-page-container">
        <div className="container py-5 text-center">
          <h3>Unable to load product</h3>
          <p>{error || 'This product is unavailable right now.'}</p>
          <Link to="/shop" className="btn-gold">Back to Shop</Link>
        </div>
        <ToastContainer toasts={toasts} />
      </div>
    );
  }

  const angles = ANGLES;

  const activeSrc = product.images?.[activeAngle] || product.images?.front || '';
  const discount = product.originalPrice
    ? Math.round(((product.originalPrice - product.price) / product.originalPrice) * 100)
    : 0;
  const selectedSizeIndex = product.sizes.indexOf(selectedSize);
  const selectedPrice = product.prices[selectedSizeIndex] ?? product.price;
  const selectedOfferPrice = product.offerPrices?.[selectedSizeIndex];
  const displayPrice = selectedOfferPrice ?? selectedPrice;

  const reviews = reviewsData[String(product.id)] || [];
  const avgRating = reviews.length
    ? (reviews.reduce((s, r) => s + r.rating, 0) / reviews.length).toFixed(1)
    : product.rating;
  const ratingDist = [5, 4, 3, 2, 1].map(n => ({ n, count: reviews.filter(r => r.rating === n).length }));
  const maxDist = Math.max(...ratingDist.map(r => r.count), 1);

  const handleAddToCart = () => {
    const selectedSizeIndex = product.sizes.indexOf(selectedSize);
    const selectedPrice = product.prices[selectedSizeIndex] ?? product.price;
    const selectedOfferPrice = product.offerPrices?.[selectedSizeIndex];
    addToCart({
      ...product,
      price: selectedOfferPrice ?? selectedPrice,
      variant: `${selectedColor} / ${selectedSize}`,
      qty,
    });
    showToast(`${product.name} added to cart!`, 'fa-bag-shopping');
  };

  const handleWishlist = async () => {
    const result = await toggleWishlist(product);
    if (result.action === 'error') {
      showToast(result.error || 'Unable to update wishlist', 'fa-circle-exclamation');
      return;
    }

    showToast(result.action === 'removed' ? 'Removed from wishlist' : 'Saved to wishlist', 'fa-heart');
  };

  const handleSubmitReview = async (event) => {
    event.preventDefault();
    setSubmitReviewMessage({ type: '', text: '' });

    if (!isAuthenticated) {
      setSubmitReviewMessage({ 
        type: 'danger', 
        text: 'Please sign in to submit a review.' 
      });
      return;
    }

    if (!reviewForm.comment.trim()) {
      setSubmitReviewMessage({ 
        type: 'danger', 
        text: 'Please write a comment for your review.' 
      });
      return;
    }

    setSubmitReviewLoading(true);

    try {
      const response = await api.post('/reviews/submit', {
        productId: safeId,
        rating: reviewForm.rating,
        comment: reviewForm.comment.trim(),
      });

      if (response.data?.success) {
        setReviewForm({ rating: 5, comment: '' });
        setSubmitReviewMessage({ 
          type: 'success', 
          text: response.data?.message || 'Thank you! Your review has been submitted.' 
        });
        showToast('Review submitted successfully!', 'fa-star');

        // Reload product after 2 seconds to show new review
        setTimeout(() => {
          window.location.reload();
        }, 2000);
      } else {
        setSubmitReviewMessage({
          type: 'danger',
          text: response.data?.message || 'Unable to submit your review right now.',
        });
      }
    } catch (error) {
      setSubmitReviewMessage({
        type: 'danger',
        text: error.response?.data?.message || 'Unable to submit your review right now.',
      });
    } finally {
      setSubmitReviewLoading(false);
    }
  };

  return (
    <div className="detail-page-container">
      {/* Breadcrumb */}
      <div className="breadcrumb-wrapper">
        <div className="container">
          <nav className="breadcrumb-nav">
            <Link to="/" className="breadcrumb-link">Home</Link>
            <i className="fas fa-chevron-right breadcrumb-arrow" />
            <Link to="/shop" className="breadcrumb-link">Shop</Link>
            <i className="fas fa-chevron-right breadcrumb-arrow" />
            <Link 
              to={`/shop?category=${slugifyCategoryName(product.category)}`} 
              className="breadcrumb-link"
            >
              {product.category}
            </Link>
            <i className="fas fa-chevron-right breadcrumb-arrow" />
            <span className="breadcrumb-current">{product.name}</span>
          </nav>
        </div>
      </div>

      <div className="container main-content-box">
        <div className="row g-5">
          {/* ── GALLERY ── */}
          <div className="col-lg-5">
            <div className="detail-gallery-sticky">
              <div className="detail-main-img" ref={wrapRef} onMouseMove={handleMouseMove}>
                <img
                  src={activeSrc}
                  alt={`${product.name} — ${ANGLES.find(a => a.key === activeAngle)?.label}`}
                  onError={e => { e.target.src = 'jewelleryImg'; }}
                />
                <div className="zoom-label">
                  <i className="fas fa-search-plus" style={{ marginRight: 5 }} />Hover to zoom
                </div>
                {product.badge && (
                  <div className="badge-gold-absolute">
                    {product.badge}
                  </div>
                )}
              </div>

              {/* Thumbnails */}
              <div className="thumb-strip">
                {angles
                  .filter(a => Boolean(product.images?.[a.key]))
                  .map(a => (
                    <div key={a.key} className="thumb-item">
                      <div
                        className={`thumb-img ${activeAngle === a.key ? 'active' : ''}`}
                        onClick={() => setActiveAngle(a.key)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={e => e.key === 'Enter' && setActiveAngle(a.key)}
                        aria-label={a.label}
                      >
                        <img
                          src={product.images[a.key]}
                          alt={a.label}
                        />
                      </div>
                      <span className="thumb-label-txt">{a.label.replace(' View', '')}</span>
                    </div>
                  ))}
              </div>
            </div>
          </div>

          {/* ── INFO ── */}
          <div className="col-lg-7">
            <div style={{ position: 'sticky', top: 90 }}>
              <div className="meta-row">
                <span className="category-eyebrow">
                  {product.category}
                </span>
                {product.badge && (
                  <span className="badge-navy">
                    {product.badge}
                  </span>
                )}
                {product.stock < 10 && (
                  <span className="stock-warning">
                    <i className="fas fa-triangle-exclamation" style={{ marginRight: 4 }} />Only {product.stock} left
                  </span>
                )}
              </div>

              <h1 className="product-main-title">
                {product.name}
              </h1>

              {/* Rating */}
              <div className="rating-row">
                <span className="stars-gold stars-gold-size">
                  {[1, 2, 3, 4, 5].map(s => <i key={s} className={`${s <= Math.round(product.rating) ? 'fas' : 'far'} fa-star`} />)}
                </span>
                <span className="rating-avg-text">{product.rating}</span>
                <button onClick={() => setActiveTab('reviews')} className="reviews-anchor-btn">
                  {product.reviews} reviews
                </button>
                {product.specifications?.Origin && (
                  <span className="origin-tag">
                    <i className="fas fa-map-marker-alt" style={{ color: 'var(--gold)', marginRight: 4 }} />
                    {product.specifications.Origin}
                  </span>
                )}
              </div>

              {/* Price */}
              <div className="price-block">
                <span className="current-price">
                  ₹{displayPrice.toLocaleString('en-IN')}
                </span>
                {selectedOfferPrice != null && selectedOfferPrice < selectedPrice && (
                  <span className="original-price">
                    ₹{selectedPrice.toLocaleString('en-IN')}
                  </span>
                )}
                {product.originalPrice && selectedOfferPrice == null && (
                  <>
                    <span className="original-price">
                      ₹{product.originalPrice.toLocaleString('en-IN')}
                    </span>
                    <span className="discount-badge">
                      {discount}% OFF
                    </span>
                  </>
                )}
              </div>

              {/* Color */}
              {product.colors[0] !== 'N/A' && (
                <div className="variant-group">
                  <p className="variant-label">Color: <span>{selectedColor}</span></p>
                  <div className="swatch-row">
                    {product.colors.map(c => (
                      <button key={c} className={`swatch ${selectedColor === c ? 'active' : ''}`} onClick={() => setSelectedColor(c)}>{c}</button>
                    ))}
                  </div>
                </div>
              )}

              {/* Size */}
              {product.sizes[0] !== 'One Size' && (
                <div className="variant-group">
                  <p className="variant-label">Size: <span>{selectedSize}</span></p>
                  <div className="swatch-row">
                    {product.sizes.map((s, index) => (
                      <button key={s} className={`swatch ${selectedSize === s ? 'active' : ''}`} onClick={() => setSelectedSize(s)}>
                        {s}
                        {/* {product.prices[index] !== undefined && ` - ₹${product.prices[index].toLocaleString('en-IN')}`} */}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Material */}
              {product.materials[0] !== 'N/A' && (
                <div className="variant-group">
                  <p className="variant-label">Material: <span>{selectedMaterial}</span></p>
                  <div className="swatch-row">
                    {product.materials.map(m => (
                      <button key={m} className={`swatch ${selectedMaterial === m ? 'active' : ''}`} onClick={() => setSelectedMaterial(m)}>{m}</button>
                    ))}
                  </div>
                </div>
              )}

              {/* Qty + CTA */}
              <div className="cta-row">
                <div className="qty-ctrl">
                  <button className="qty-btn" onClick={() => setQty(q => Math.max(1, q - 1))}>−</button>
                  <span className="qty-val">{qty}</span>
                  <button className="qty-btn" onClick={() => setQty(q => Math.min(product.stock, q + 1))}>+</button>
                </div>
                <button className="btn-gold btn-add-to-cart" onClick={handleAddToCart}>
                  <i className="fas fa-bag-shopping" /> Add to Cart
                </button>
                <button
                  onClick={handleWishlist}
                  className="btn-wishlist-toggle"
                  style={{ color: isInWishlist(product.id) ? '#e74c3c' : 'var(--text-light)' }}
                  aria-label="Toggle wishlist"
                >
                  <i className={`${isInWishlist(product.id) ? 'fas' : 'far'} fa-heart`} />
                </button>
              </div>

              {/* Trust badges */}
              <div className="trust-badges-container">
                {[
                  { icon: 'fa-truck', text: `${product.shippingClass} shipping` },
                  { icon: 'fa-rotate-left', text: `${product.returnDays}-day returns` },
                  { icon: 'fa-shield-halved', text: 'Secure payment' },
                ].map((t, i) => (
                  <div key={i} className="trust-badge-item">
                    <i className={`fas ${t.icon}`} style={{ color: 'var(--gold)' }} />{t.text}
                  </div>
                ))}
              </div>

              {/* Features */}
              <div className="features-wrap">
                {product.features.map(f => (
                  <span key={f} className="badge-feature">
                    <i className="fas fa-check" style={{ color: 'var(--gold)', marginRight: 5, fontSize: '0.65rem' }} />{f}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* ── TABS: Description / Specs / Reviews ── */}
        <div className="tabs-section-space">
          <div className="product-tabs">
            {['description', 'specifications', 'reviews'].map(tab => (
              <button key={tab} className={`product-tab ${activeTab === tab ? 'active' : ''}`} onClick={() => setActiveTab(tab)}>
                {tab === 'description' && 'Description'}
                {tab === 'specifications' && 'Specifications'}
                {tab === 'reviews' && `Reviews (${reviews.length || product.reviews})`}
              </button>
            ))}
          </div>

          {activeTab === 'description' && (
            <div className="tab-content-max-w">
              <p className="description-text">{product.description}</p>
              <ul className="description-bullets">
                {product.features.map(f => (
                  <li key={f} className="description-bullet-item">
                    <strong className="description-bullet-bold">{f}</strong>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {activeTab === 'specifications' && product.specifications && (
            <div className="specs-table-max-w">
              <table className="specs-table">
                <tbody>
                  {Object.entries(product.specifications).map(([k, v]) => (
                    <tr key={k}><td>{k}</td><td>{v}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {activeTab === 'reviews' && (
            <div className="reviews-section-max-w">
              <div className="rating-summary">
                <div>
                  <div className="rating-big">{avgRating}<span className="rating-big-subtext">/5</span></div>
                  <div className="stars-gold rating-stars-margin">
                    {[1, 2, 3, 4, 5].map(s => <i key={s} className={`${s <= Math.round(avgRating) ? 'fas' : 'far'} fa-star`} style={{ fontSize: '0.9rem' }} />)}
                  </div>
                  <p className="rating-count-label">{reviews.length || product.reviews} reviews</p>
                </div>
                <div className="rating-bars">
                  {ratingDist.map(r => (
                    <StarBar key={r.n} label={r.n} count={r.count} max={maxDist} />
                  ))}
                </div>
              </div>

              {/* All Reviews Section */}
              <div style={{ marginBottom: '2rem' }}>
                <p className="profile-card-title" style={{ marginBottom: '1rem' }}>
                  <i className="fas fa-comments" style={{ marginRight: 8 }} />Customer Reviews
                </p>
                {reviews.length > 0 || (isAuthenticated && userReviews.length > 0) ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    {/* Display User's Reviews First */}
                    {isAuthenticated && userReviews.map(myReview => (
                      <div key={myReview.id} style={{
                        padding: '1rem',
                        border: '2px solid #d4a574',
                        borderRadius: '6px',
                        backgroundColor: '#fef9f3',
                        position: 'relative'
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '0.75rem' }}>
                          <div style={{ flex: 1 }}>
                            <span style={{
                              display: 'inline-block',
                              padding: '0.3rem 0.8rem',
                              borderRadius: '20px',
                              fontSize: '0.75rem',
                              fontWeight: 600,
                              backgroundColor: '#d4a574',
                              color: '#fff',
                              marginBottom: '0.5rem',
                              marginRight: '0.5rem'
                            }}>
                              ✓ It's Yours
                            </span>
                            <span style={{
                              display: 'inline-block',
                              padding: '0.3rem 0.8rem',
                              borderRadius: '20px',
                              fontSize: '0.75rem',
                              fontWeight: 500,
                              backgroundColor: myReview.isActive ? '#e8f5e9' : '#fff3e0',
                              color: myReview.isActive ? '#2e7d32' : '#f57c00',
                            }}>
                              {/* {myReview.isActive ? '✓ Approved' : '⏳ Pending'} */}
                            </span>
                          </div>
                        </div>

                        <p style={{ fontSize: '0.85rem', color: '#666', margin: '0.5rem 0 0.75rem 0', fontWeight: 500 }}>
                          {formatReviewDate(myReview.createdAt)}
                        </p>

                        <div className="stars-gold" style={{ marginBottom: '0.5rem', fontSize: '0.9rem' }}>
                          {[1, 2, 3, 4, 5].map(s => <i key={s} className={`${s <= myReview.rating ? 'fas' : 'far'} fa-star`} />)}
                        </div>

                        <p style={{ margin: '0.5rem 0', color: '#333', lineHeight: '1.5' }}>
                          {myReview.comment}
                        </p>
                      </div>
                    ))}

                    {/* Display Other Customer Reviews */}
                    {reviews.map(r => (
                      <div key={r.id} className="review-card">
                        <div className="review-header">
                          <div className="review-avatar">{r.avatar}</div>
                          <div style={{ flex: 1 }}>
                            <div className="review-meta-group">
                              <span className="review-name">{r.name}</span>
                              {r.verified && <span className="review-verified"><i className="fas fa-circle-check" />Verified Purchase</span>}
                              <span className="review-date">{r.date}</span>
                            </div>
                            <div className="stars-gold review-stars-margin">
                              {[1, 2, 3, 4, 5].map(s => <i key={s} className={`${s <= r.rating ? 'fas' : 'far'} fa-star`} />)}
                            </div>
                          </div>
                        </div>
                        <p className="review-text">{r.comment}</p>
                        <div className="review-helpful">
                          Helpful? <button><i className="fas fa-thumbs-up" style={{ marginRight: 4 }} />{r.helpful}</button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="no-reviews-fallback">No reviews yet. Be the first to review!</p>
                )}
              </div>

              {/* Submit Review Form */}
              <div className="review-submit-form-container" style={{ marginBottom: '2rem', padding: '1.5rem', border: '1px solid #e0e0e0', borderRadius: '8px', backgroundColor: '#f9f9f9' }}>
                <p className="profile-card-title" style={{ marginBottom: '1rem' }}>
                  <i className="fas fa-pen-to-square" style={{ marginRight: 8 }} />Share Your Review
                </p>
                
                {!isAuthenticated ? (
                  <div style={{ textAlign: 'center', padding: '1rem' }}>
                    <p style={{ marginBottom: '1rem', color: '#666' }}>Please sign in to submit a review</p>
                    <Link to="/login" className="btn-gold">Sign In</Link>
                  </div>
                ) : (
                  <>
                    {submitReviewMessage.text && (
                      <div className={`alert alert-${submitReviewMessage.type} py-2 mb-3`} role="alert">
                        {submitReviewMessage.text}
                      </div>
                    )}
                    <form onSubmit={handleSubmitReview}>
                      <div className="row g-3">
                        {/* Star Rating Input */}
                        <div className="col-12">
                          <label className="settings-label">Your Rating *</label>
                          <div className="star-rating-input" style={{ display: 'flex', gap: '0.5rem', fontSize: '1.5rem' }}>
                            {[1, 2, 3, 4, 5].map(star => (
                              <button
                                key={star}
                                type="button"
                                onClick={() => setReviewForm(f => ({ ...f, rating: star }))}
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  cursor: 'pointer',
                                  color: star <= reviewForm.rating ? '#d4a574' : '#ccc',
                                  transition: 'color 0.2s',
                                  padding: '0.25rem',
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.color = '#d4a574';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.color = star <= reviewForm.rating ? '#d4a574' : '#ccc';
                                }}
                                aria-label={`Rate ${star} stars`}
                              >
                                <i className="fas fa-star" />
                              </button>
                            ))}
                            <span style={{ marginLeft: '0.5rem', fontSize: '0.9rem', color: '#666' }}>
                              {reviewForm.rating} out of 5
                            </span>
                          </div>
                        </div>

                        {/* Comment Input */}
                        <div className="col-12">
                          <label className="settings-label">Your Review *</label>
                          <textarea
                            className="settings-input"
                            style={{
                              minHeight: '120px',
                              fontFamily: 'inherit',
                              resize: 'vertical',
                            }}
                            placeholder="Share your experience with this product..."
                            value={reviewForm.comment}
                            onChange={(e) => setReviewForm(f => ({ ...f, comment: e.target.value }))}
                            maxLength={500}
                            required
                          />
                          <p style={{ fontSize: '0.85rem', color: '#999', marginTop: '0.5rem' }}>
                            {reviewForm.comment.length}/500 characters
                          </p>
                        </div>

                        {/* Submit Button */}
                        <div className="col-12">
                          <button
                            className="btn-gold"
                            type="submit"
                            disabled={submitReviewLoading}
                            style={{ width: '100%' }}
                          >
                            {submitReviewLoading ? (
                              <>
                                <i className="fas fa-spinner fa-spin" style={{ marginRight: 8 }} />
                                Submitting...
                              </>
                            ) : (
                              <>
                                <i className="fas fa-check" style={{ marginRight: 8 }} />
                                Submit Review
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    </form>
                  </>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Related products */}
        {related.length > 0 && (
          <div className="related-products-space">
            <div className="section-header reveal">
              <p className="section-eyebrow">You May Also Like</p>
              <h2 className="section-title">Related <em>Products</em></h2>
            </div>
            <div className="row g-4">
              {related.map(p => (
                <div key={p.id} className="col-6 col-md-4 col-lg-3">
                  <ProductCard product={p} onAddToCart={() => showToast('Added to cart!', 'fa-bag-shopping')} />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      <ToastContainer toasts={toasts} />
    </div>
  );
}